import { Component, EventEmitter, OnInit, OnDestroy, Output, effect } from '@angular/core';
import { MultiSelectChangeEvent, MultiSelectFilterEvent } from 'primeng/multiselect';
import { ItemsInventoryService, InventoryViewMode } from '../../../services/base-actions/items-inventory.service';
import { InventorySummaryProxyService } from '../../../proxies/inventory/inventory-summary.proxy.service';
import { LotItemProxyService } from '../../../proxies/inventory/lot-item.proxy.service';
import { MenuItem, TreeNode } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import {
  FilterField,
  InventoryFilters,
  getFiltersForViewMode,
  filtersTranslations,
  filtersTooltipTranslations,
} from './inventory-filters.utils';
import { InvoiceProxyService } from '../../../proxies/order/invoice.proxy.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { Translation } from '../../../models/translation';
import { Business, BusinessEntityType, BusinessSearch } from '../../../models/finance/business';
import { LocalHelperService } from '../../../services/local-helper.service';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';
import { applyLocationPaths, getDistinctLocationCodes } from '@card-software/nexus-utilities';
import { LocationNode, LocationPath, BuildPathFromCode } from '../../../models/location';
import { LotItemSearchCommand, LotItemSearchResultItem } from '../../../models/inventory/lot-item-search';
import { InventorySummarySearchCommand, InventorySummarySearchResultItem } from '../../../models/inventory/inventory-summary-search';

@Component({
  selector: 'app-items-inventory-filters',
  templateUrl: './items-inventory-filters.component.html',
  styleUrl: './items-inventory-filters.component.scss',
})
export class ItemsInventoryFiltersComponent implements OnInit, OnDestroy {
  // Item Master Consumables
  showConsumablesOnly = false;

  // Inventory Table Locations
  locations: LocationNode[] = [];
  locationTreeNodes: TreeNode[] = [];
  selectedLocationNodes: TreeNode[] = [];

  // customFilters
  customFilters: FilterField[] = [];
  filtersItems: MenuItem[] = [];
  activeFilters: FilterField[] = [];

  // Current filters based on view mode
  lotItemFilters: LotItemSearchCommand = {};
  inventorySummaryFilters: InventorySummarySearchCommand = {};

  // Boolean filters for Lot Item view (always visible)
  excludeZeroBalances = false;
  excludeSplitLotItems = false;

  // Debounce subject for search - waits 2 seconds after typing stops
  private searchSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  private isSearching = false;

  // Debounce subjects for autocomplete filter searches (to prevent multiple API calls while typing)
  private autocompleteSearchSubjects = new Map<InventoryFilters, Subject<string>>();
  private readonly AUTOCOMPLETE_DEBOUNCE_MS = 500;

  // select inputs options
  lotItemsOptions: { name: string; id: string }[] = [];
  providersOptions: Business[] = [];
  manufacturersOptions: Business[] = [];
  providersLotIds: string[] = [];
  selectedProviderLotIds: string[] = [];
  providersLotIdsOptions: { name: string; id: string }[] = [];
  manufacturerLabelOptions: { name: string; id: string }[] = [];
  itemsOptions: { name: string; id: string }[] = [];
  invoicesOptions: { name: string; id: string }[] = [];
  deliveryNoteOptions: { name: string; id: string }[] = [];

  @Output() isLoading = new EventEmitter<number>();
  @Output() totalRecords = new EventEmitter<number>();
  @Output() searched = new EventEmitter<void>();

  constructor(
    private locationProxyService: LocationProxyService,
    private inventorySummaryProxyService: InventorySummaryProxyService,
    private lotItemProxyService: LotItemProxyService,
    private itemsInventoryService: ItemsInventoryService,
    private itemMasterProxyService: ItemMasterProxyService,
    private invoiceProxyService: InvoiceProxyService,
    private businessProxyService: BusinessProxyService,
    private translateService: TranslateService,
    public localHelperTranslate: LocalHelperService,
  ) {
    this.updateFiltersForViewMode();

    this.translateService.onLangChange.subscribe(() => {
      this.updateFiltersForViewMode();
      this.updateFiltersItems();
    });

    // React to view mode changes
    effect(() => {
      const viewMode = this.itemsInventoryService.viewMode();
      this.onViewModeChanged(viewMode);
    });

    // Setup debounced search - waits 2 seconds after last trigger, blocks during search
    this.searchSubject.pipe(takeUntil(this.destroy$), debounceTime(2000)).subscribe(() => {
      if (!this.isSearching) {
        this.executeSearch();
      }
    });

    // Setup debounced autocomplete searches
    this.setupAutocompleteDebounce();
  }

  /**
   * Setup debounced subjects for each autocomplete filter type
   */
  private setupAutocompleteDebounce(): void {
    const filterKeys: InventoryFilters[] = [
      'lotItemNumbers',
      'itemCodes',
      'providerReferences',
      'manufacturerReferences',
      'invoiceReferences',
    ];

    filterKeys.forEach((key) => {
      const subject = new Subject<string>();
      this.autocompleteSearchSubjects.set(key, subject);

      subject.pipe(takeUntil(this.destroy$), debounceTime(this.AUTOCOMPLETE_DEBOUNCE_MS)).subscribe((query) => {
        this.executeAutocompleteSearch(key, query);
      });
    });
  }

  /**
   * Execute the actual autocomplete search for a specific filter
   */
  private executeAutocompleteSearch(key: InventoryFilters, query: string): void {
    switch (key) {
      case 'providerReferences':
        this.getProviders(query);
        break;
      case 'manufacturerReferences':
        this.getManufacturers(query);
        break;
      case 'itemCodes':
        this.getItems(query);
        break;
      case 'invoiceReferences':
        this.getInvoices(query);
        break;
      case 'lotItemNumbers':
        this.getLotItems(query);
        break;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currentLanguage(): string {
    return this.translateService.currentLang;
  }

  get currentViewMode(): InventoryViewMode {
    return this.itemsInventoryService.viewMode();
  }

  getTranslation(key: string): string {
    return filtersTranslations[key]?.[this.currentLanguage as keyof Translation] || key;
  }

  getTooltip(key: string): string {
    return filtersTooltipTranslations[key]?.[this.currentLanguage as keyof Translation] || this.getTranslation(key);
  }

  ngOnInit(): void {
    this.getLocations();
    this.updateFiltersItems();
    this.getManufacturers('');
    this.getProviders('');
  }

  /**
   * Update available filters based on current view mode
   */
  private updateFiltersForViewMode(): void {
    this.customFilters = getFiltersForViewMode(this.currentViewMode, this.translateService);
  }

  /**
   * Handle view mode changes - clear active filters and reset
   */
  private onViewModeChanged(viewMode: InventoryViewMode): void {
    // Clear active filters when view mode changes
    this.activeFilters = [];
    this.updateFiltersForViewMode();
    this.updateFiltersItems();

    // Clear filters
    if (viewMode === 'lotItem') {
      this.showConsumablesOnly = false;
      this.lotItemFilters = {};
      this.itemsInventoryService.setLotItemSearchFilters({});
    } else {
      this.showConsumablesOnly = true;
      this.inventorySummaryFilters = {};
      this.itemsInventoryService.setInventorySummarySearchFilters({});
    }

    // Re-apply location filter if locations are selected
    if (this.selectedLocationNodes.length > 0) {
      this.applyLocationFilter();
    }
  }

  /**
   * Trigger search with debounce - waits 2 seconds after last call
   * Use this for filter changes that happen while typing
   */
  triggerSearchDebounced(): void {
    this.resetSearchPage();
    this.searchSubject.next();
  }

  /**
   * Trigger search immediately - use for explicit user actions like button clicks.
   * Pass resetPage=true when triggered by filter changes (resets to page 1).
   * Parent page navigation calls this without resetPage so the current page is preserved.
   */
  triggerSearch(resetPage = false): void {
    if (resetPage) {
      this.resetSearchPage();
    }
    if (!this.isSearching) {
      this.executeSearch();
    }
  }

  private resetSearchPage(): void {
    this.lotItemProxyService.lotItemSearchPage.set(1);
    this.inventorySummaryProxyService.inventorySummarySearchPage.set(1);
  }

  /**
   * Execute the actual search based on current view mode
   */
  private executeSearch(): void {
    this.isSearching = true;
    this.isLoading.emit(1);

    if (this.currentViewMode === 'lotItem') {
      this.searchLotItems();
    } else {
      this.searchInventorySummary();
    }
  }

  private searchLotItems(): void {
    const filters = this.itemsInventoryService.lotItemSearchFilters();
    const page = this.lotItemProxyService.lotItemSearchPage();
    const pageSize = this.lotItemProxyService.lotItemSearchPageSize();

    this.lotItemProxyService.search(filters, page, pageSize).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.itemsInventoryService.setLotItemResults(response.data.items);
          this.totalRecords.emit(response.data.totalCount);
          this.enrichLotItemLocationPaths(response.data.items);
        } else {
          this.itemsInventoryService.setLotItemResults([]);
          this.totalRecords.emit(0);
        }
        this.isLoading.emit(0);
        this.isSearching = false;
        this.searched.emit();
      },
      error: (error) => {
        console.error('Error searching lot items:', error);
        this.itemsInventoryService.setLotItemResults([]);
        this.totalRecords.emit(0);
        this.isLoading.emit(0);
        this.isSearching = false;
      },
    });
  }

  private searchInventorySummary(): void {
    const filters = this.itemsInventoryService.inventorySummarySearchFilters();
    const page = this.inventorySummaryProxyService.inventorySummarySearchPage();
    const pageSize = this.inventorySummaryProxyService.inventorySummarySearchPageSize();

    this.inventorySummaryProxyService.search(filters, page, pageSize).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.itemsInventoryService.setInventorySummaryResults(response.data.items);
          this.totalRecords.emit(response.data.totalCount);
          this.enrichInventorySummaryLocationPaths(response.data.items);
        } else {
          this.itemsInventoryService.setInventorySummaryResults([]);
          this.totalRecords.emit(0);
        }
        this.isLoading.emit(0);
        this.isSearching = false;
        this.searched.emit();
      },
      error: (error) => {
        console.error('Error searching inventory summary:', error);
        this.itemsInventoryService.setInventorySummaryResults([]);
        this.totalRecords.emit(0);
        this.isLoading.emit(0);
        this.isSearching = false;
      },
    });
  }

  /**
   * Resolves the full location path (breadcrumb) for the lot items on the current page, the same way
   * Roll Production Inventory does: one batch call to the Inventory location endpoint with detailed = true.
   * currentLocationName is left untouched as the simple-name fallback.
   */
  private enrichLotItemLocationPaths(items: LotItemSearchResultItem[]): void {
    const codes = getDistinctLocationCodes(items, (i) => i.currentLocationCode);
    if (codes.length === 0) return;

    this.locationProxyService.batchGetByCodes(codes, true).subscribe({
      next: (response) => {
        // Skip if a newer search already replaced these results (e.g. fast paging).
        if (this.itemsInventoryService.lotItemResults() !== items) return;
        applyLocationPaths(
          items,
          response?.data,
          (i) => i.currentLocationCode,
          (i, location) => (i.currentLocationPathNames = location.pathNames!),
        );
        // Re-set to trigger change detection now that the items were enriched.
        this.itemsInventoryService.setLotItemResults([...items]);
      },
      error: (error) => console.error('Error resolving lot item location paths:', error),
    });
  }

  /**
   * Same as enrichLotItemLocationPaths, for the Consumables (inventory summary) tab.
   */
  private enrichInventorySummaryLocationPaths(items: InventorySummarySearchResultItem[]): void {
    const codes = getDistinctLocationCodes(items, (i) => i.locationCode);
    if (codes.length === 0) return;

    this.locationProxyService.batchGetByCodes(codes, true).subscribe({
      next: (response) => {
        if (this.itemsInventoryService.inventorySummaryResults() !== items) return;
        applyLocationPaths(
          items,
          response?.data,
          (i) => i.locationCode,
          (i, location) => (i.locationPathNames = location.pathNames!),
        );
        this.itemsInventoryService.setInventorySummaryResults([...items]);
      },
      error: (error) => console.error('Error resolving inventory summary location paths:', error),
    });
  }

  // *** Location TreeSelect Methods *** ///
  getLocations(): void {
    this.locationProxyService.getAll().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.locations = response.data;
          this.locationTreeNodes = this.buildLocationTreeStructure(response.data);
          this.itemsInventoryService.setSelectedLocations(response.data);

          this.triggerSearch(true);
        }
      },
      error: (error) => {
        console.error('Error loading locations:', error);
      },
    });
  }

  /**
   * Auto-select the location with code "NT" by default
   */
  private autoSelectDefaultLocation(locations: LocationNode[]): void {
    const ntLocation = locations.find((loc) => loc.code === 'NT');
    if (ntLocation) {
      // Find the tree node for NT
      const findTreeNode = (nodes: TreeNode[], code: string): TreeNode | undefined => {
        for (const node of nodes) {
          const location = node.data as LocationNode;
          if (location.code === code) {
            return node;
          }
          if (node.children && node.children.length > 0) {
            const found = findTreeNode(node.children, code);
            if (found) return found;
          }
        }
        return undefined;
      };

      const ntNode = findTreeNode(this.locationTreeNodes, 'NT');
      if (ntNode) {
        this.selectedLocationNodes = [ntNode];
        this.applyLocationFilter();
        this.triggerSearch(true);
      }
    }
  }

  /**
   * Build tree structure from flat location list using parentCode relationships
   */
  private buildLocationTreeStructure(locations: LocationNode[]): TreeNode[] {
    if (!locations || locations.length === 0) {
      return [];
    }

    const locationMap = new Map<string, TreeNode>();
    const rootNodes: TreeNode[] = [];

    // First pass: create all tree nodes
    locations.forEach((location) => {
      locationMap.set(location.locationCode, {
        key: location.locationCode,
        label: `${location.code} - ${this.localHelperTranslate.getLocalizedValue(location.name)}`,
        data: location,
        children: [],
        expanded: false,
        selectable: true,
      });
    });

    // Second pass: build the hierarchy using parentCode
    locations.forEach((location) => {
      const treeNode = locationMap.get(location.locationCode);
      if (!treeNode) return;

      if (location.parentCode) {
        // Has a parent, add to parent's children
        const parentNode = locationMap.get(location.parentCode);
        if (parentNode) {
          if (!parentNode.children) {
            parentNode.children = [];
          }
          parentNode.children.push(treeNode);
        } else {
          // Parent not found, treat as root
          rootNodes.push(treeNode);
        }
      } else {
        // No parent, this is a root node
        rootNodes.push(treeNode);
      }
    });

    return rootNodes;
  }

  /**
   * Handle TreeSelect location selection change
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onLocationTreeSelectChange(event: unknown): void {
    this.applyLocationFilter();
    this.triggerSearch(true);
  }

  /**
   * Apply location filter to current view mode filters
   */
  private applyLocationFilter(): void {
    const selectedNodes: TreeNode[] = this.selectedLocationNodes || [];

    // Debug logging to identify filter issue
    console.log('[Location Filter] Selected nodes:', selectedNodes);
    console.log(
      '[Location Filter] Selected nodes data:',
      selectedNodes.map((n) => n.data),
    );

    // Extract LocationPaths from selected nodes, using BuildPathFromCode as fallback
    const locationPaths: LocationPath[] = selectedNodes
      .map((node) => {
        const location = node.data as LocationNode;
        if (!location || !location.locationCode) {
          console.warn('[Location Filter] Node missing data or locationCode:', node);
          return undefined;
        }
        const path = location.path ?? BuildPathFromCode(location.locationCode);
        console.log('[Location Filter] Location:', location.locationCode, '-> Path:', path);
        return path;
      })
      .filter((path): path is LocationPath => path !== undefined);

    console.log('[Location Filter] Final locationPaths:', locationPaths);

    if (this.currentViewMode === 'lotItem') {
      if (locationPaths.length === 0) {
        delete this.lotItemFilters.locationPaths;
      } else {
        this.lotItemFilters = { ...this.lotItemFilters, locationPaths };
      }
      this.itemsInventoryService.setLotItemSearchFilters(this.lotItemFilters);
    } else {
      if (locationPaths.length === 0) {
        delete this.inventorySummaryFilters.locationPaths;
      } else {
        this.inventorySummaryFilters = { ...this.inventorySummaryFilters, locationPaths };
      }
      this.itemsInventoryService.setInventorySummarySearchFilters(this.inventorySummaryFilters);
    }
  }

  // *** Dynamic Filters Actions *** ///
  showFilterInput(customField: FilterField): void {
    this.activeFilters.push(customField);
    this.updateFiltersItems();
  }

  updateFiltersItems(): void {
    this.filtersItems = this.customFilters
      .filter((filter) => !this.activeFilters.some((active) => active.name === filter.name))
      .map((customField) => ({
        label: this.getTranslation(customField.name),
        command: () => this.showFilterInput(customField),
        disabled: false,
      }));
  }

  removeFilter(customField: FilterField): void {
    this.activeFilters = this.activeFilters.filter((f) => f.name !== customField.name);

    // Clear the filter value
    this.updateFilter(customField.name, undefined);

    // Special case for manufacturerLotNumbers
    if (customField.name === 'manufacturerLotNumbers') {
      this.selectedProviderLotIds = [];
    }

    this.updateFiltersItems();
    this.triggerSearch(true);
  }

  /**
   * Get the count of selected values for a filter
   */
  getFilterSelectedCount(filterName: InventoryFilters): number {
    const currentFilters = this.currentViewMode === 'lotItem' ? this.lotItemFilters : this.inventorySummaryFilters;
    const values = (currentFilters as Record<string, unknown>)[filterName];
    if (Array.isArray(values)) {
      return values.length;
    }
    return 0;
  }

  /**
   * Check if a filter has selected values
   */
  hasFilterValues(filterName: InventoryFilters): boolean {
    return this.getFilterSelectedCount(filterName) > 0;
  }

  // *** Boolean Filter Actions (Lot Item view only) *** ///
  onExcludeZeroBalancesChange(): void {
    this.lotItemFilters = { ...this.lotItemFilters, excludeZeroBalances: this.excludeZeroBalances || undefined };
    if (!this.excludeZeroBalances) {
      delete this.lotItemFilters.excludeZeroBalances;
    }
    this.itemsInventoryService.setLotItemSearchFilters(this.lotItemFilters);
    this.triggerSearchDebounced();
  }

  onExcludeSplitLotItemsChange(): void {
    this.lotItemFilters = { ...this.lotItemFilters, excludeSplitLotItems: this.excludeSplitLotItems || undefined };
    if (!this.excludeSplitLotItems) {
      delete this.lotItemFilters.excludeSplitLotItems;
    }
    this.itemsInventoryService.setLotItemSearchFilters(this.lotItemFilters);
    this.triggerSearchDebounced();
  }

  // *** Filter Actions *** ///
  onAutocompleteChange(key: InventoryFilters, event: MultiSelectChangeEvent): void {
    this.updateFilter(key, event.value.length === 0 ? undefined : event.value);

    // If manufacturers are selected, update lot ID options
    if (key === 'manufacturerReferences') {
      this.setManufacturersLotIds(event.itemValue, event.value);
    }

    this.triggerSearchDebounced();
  }

  /**
   * Update filter value based on current view mode
   */
  private updateFilter(key: InventoryFilters, value: unknown): void {
    if (this.currentViewMode === 'lotItem') {
      if (value === undefined) {
        delete (this.lotItemFilters as Record<string, unknown>)[key];
      } else {
        (this.lotItemFilters as Record<string, unknown>)[key] = value;
      }
      this.itemsInventoryService.setLotItemSearchFilters(this.lotItemFilters);
    } else {
      // Inventory Summary only supports itemCodes and locationPaths
      if (key === 'itemCodes') {
        if (value === undefined) {
          delete this.inventorySummaryFilters.itemCodes;
        } else {
          this.inventorySummaryFilters.itemCodes = value as string[];
        }
        this.itemsInventoryService.setInventorySummarySearchFilters(this.inventorySummaryFilters);
      }
    }
  }

  // *** Search Autocomplete Options *** ///
  searchAutocompleteOptions(key: InventoryFilters, event: MultiSelectFilterEvent): void {
    const query = event.filter;

    // For manufacturerLotNumbers, search is local (no API call needed)
    if (key === 'manufacturerLotNumbers') {
      this.getManufacturersLotIds(query);
      return;
    }

    // Use debounced subject for API calls
    const subject = this.autocompleteSearchSubjects.get(key);
    if (subject) {
      subject.next(query);
    }
  }

  getFilterOptions(key: InventoryFilters): { name: string; id: string }[] | Business[] {
    switch (key) {
      case 'providerReferences':
        return this.providersOptions;
      case 'manufacturerReferences':
        return this.manufacturersOptions;
      case 'itemCodes':
        return this.itemsOptions;
      case 'invoiceReferences':
        return this.invoicesOptions;
      case 'manufacturerLotNumbers':
        return this.providersLotIdsOptions;
      case 'manufacturerLabelNumbers':
        return this.manufacturerLabelOptions;
      case 'lotItemNumbers':
        return this.lotItemsOptions;
      case 'deliveryNoteReferences':
        return this.deliveryNoteOptions;
      default:
        return [];
    }
  }

  // *** Data Fetching Methods *** ///
  getLotItems(query: string): void {
    this.lotItemProxyService.search({ lotItemNumbers: query ? [query] : undefined }, 1, 50).subscribe({
      next: (response) => {
        if (response.success && response.data?.items) {
          this.lotItemsOptions = response.data.items.map((lotItem: LotItemSearchResultItem) => ({
            id: lotItem.number!,
            name: lotItem.number!,
          }));
          this.preserveExistingSelections('lotItemNumbers');
        }
      },
      error: (error) => {
        console.error('Error searching lot items:', error);
      },
    });
  }

  getProviders(query: string): void {
    const command: BusinessSearch = {
      name: query,
      businessTypes: [BusinessEntityType.Provider],
    };

    this.businessProxyService.search(command, 1, 50).subscribe({
      next: (response) => {
        if (response.success && response.data?.items) {
          this.providersOptions = response.data.items;
          this.preserveExistingSelections('providerReferences');
        }
      },
      error: (error) => {
        console.error('Error searching providers:', error);
      },
    });
  }

  getManufacturers(query: string): void {
    const command: BusinessSearch = {
      name: query,
      businessTypes: [BusinessEntityType.Manufacturer],
    };

    this.businessProxyService.search(command, 1, 50).subscribe({
      next: (response) => {
        if (response.success && response.data?.items) {
          this.manufacturersOptions = response.data.items;

          if (this.manufacturersOptions.length > 0) {
            // Collect lot numbers from manufacturers
            this.providersLotIds = Array.from(new Set(response.data.items.flatMap((mfg) => mfg.lotNumbers ?? [])));
          }

          this.preserveExistingSelections('manufacturerReferences');
        }
      },
      error: (error) => {
        console.error('Error searching manufacturers:', error);
      },
    });
  }

  getItems(query: string): void {
    this.itemMasterProxyService.search({ name: query, itemCode: query }, true, this.showConsumablesOnly).subscribe({
      next: (response) => {
        if (response.success && response.data?.items) {
          this.itemsOptions = response.data?.items.map((item) => ({
            id: item.itemCode,
            name: `(${item.itemCode}) ${this.localHelperTranslate.getLocalizedValue(item.itemDisplayName)}`,
          }));
          this.preserveExistingSelections('itemCodes');
        }
      },
      error: (error) => {
        console.error('Error searching items:', error);
      },
    });
  }

  getInvoices(query: string): void {
    this.invoiceProxyService.searchInvoice({ invoiceNumber: query }, 1, 50).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.invoicesOptions = response.data.items.map((invoice) => ({
            id: invoice.id!,
            name: invoice.invoiceNumber,
          }));
          this.preserveExistingSelections('invoiceReferences');
        }
      },
      error: (error) => {
        console.error('Error searching invoices:', error);
      },
    });
  }

  setManufacturersLotIds(selectedManufacturer: Business, manufacturerReferences: string[]): void {
    if (manufacturerReferences.includes(selectedManufacturer.id!)) {
      // Add lot numbers from this manufacturer
      const merged = [...this.providersLotIds, ...(selectedManufacturer.lotNumbers ?? [])];
      this.providersLotIds = Array.from(new Set(merged));
    } else {
      // Remove lot numbers from this manufacturer
      this.providersLotIds = this.providersLotIds.filter((lotId) => !(selectedManufacturer.lotNumbers ?? []).includes(lotId));

      this.selectedProviderLotIds = this.selectedProviderLotIds.filter((lotId) => !(selectedManufacturer.lotNumbers ?? []).includes(lotId));

      if (this.selectedProviderLotIds.length > 0) {
        this.updateFilter('manufacturerLotNumbers', this.selectedProviderLotIds);
      }
    }

    // Update visible options
    if (this.providersLotIds.length === 0) {
      this.providersLotIdsOptions = [];
      this.selectedProviderLotIds = [];
      this.updateFilter('manufacturerLotNumbers', undefined);
    } else {
      this.providersLotIdsOptions = Array.from(new Set(this.providersLotIds)).map((lotId) => ({
        id: lotId,
        name: lotId,
      }));
    }
  }

  getManufacturersLotIds(query: string): void {
    if (!query || query.trim() === '') {
      this.providersLotIdsOptions = Array.from(new Set(this.providersLotIds)).map((lotId) => ({
        id: lotId,
        name: lotId,
      }));
    } else {
      const filteredLotIds = this.providersLotIds.filter((lotId) => lotId.toLowerCase().includes(query.toLowerCase()));

      const selectedNotInFilter = this.selectedProviderLotIds.filter(
        (selected) => !filteredLotIds.includes(selected) && this.providersLotIds.includes(selected),
      );

      const allToShow = [...new Set([...filteredLotIds, ...selectedNotInFilter])];

      this.providersLotIdsOptions = allToShow.map((lotId) => ({
        id: lotId,
        name: lotId,
      }));
    }
  }

  private preserveExistingSelections(filterKey: InventoryFilters): void {
    const currentFilters = this.currentViewMode === 'lotItem' ? this.lotItemFilters : this.inventorySummaryFilters;
    const currentSelections = (currentFilters as Record<string, unknown>)[filterKey];

    if (currentSelections && Array.isArray(currentSelections)) {
      const currentOptions = this.getFilterOptions(filterKey) as { id: string; name: string }[];
      const selectedNotInOptions = currentSelections.filter((selected) => !currentOptions.some((option) => option.id === selected));

      selectedNotInOptions.forEach((selected) => {
        currentOptions.push({
          id: selected,
          name: selected,
        } as { name: string; id: string });
      });
    }
  }
}
